import { devSimulatePing } from '~/rage/decorators';
import {
	Gender,
	IDeathScreenSession,
	IPlayerJailInfo,
	IPlayerLevel,
	ModalButtonColor,
	ModalType,
	sleep,
	WalletType,
} from '~/shared';
import { IObservable, Subject } from '../../utils';
import { IModalService } from '../ModalService';
import { IPlayerService, IPriceConfig } from './playerServiceTypes';

export class PlayerServiceStub implements IPlayerService {
	private readonly accountId = new Subject('125');
	private readonly characterId = new Subject('878');
	private readonly netId = new Subject(958);
	private readonly premium = new Subject(true);
	private readonly premiumDays = new Subject(12);
	private readonly registrationDate = new Subject(1714657600000);
	private readonly parkingSlotsPrice = new Subject(1000);
	private readonly changeNamePrice = new Subject(1000);
	private readonly parkingSlotPriceConfig = new Subject<IPriceConfig>({
		price: 1000,
		discount: null,
		discountTimestampStart: null,
		discountTimestampEnd: null,
	});
	private readonly changeNamePriceConfig = new Subject<IPriceConfig>({
		price: 1000,
		discount: null,
		discountTimestampStart: null,
		discountTimestampEnd: null,
	});
	private readonly playerJailInfo = new Subject<IPlayerJailInfo | null>({
		expiresAt: Date.now() + 1000 * 60 * 60 * 20,
		reason: 'Украл машину у Максима и продал Владу',
	});
	private readonly nickname = new Subject('IMineev');
	private readonly maskedEmail = new Subject<string>('tackti***@gmail.com');
	private readonly usedParkingSlots = new Subject<number>(22);
	private readonly totalParkingSlots = new Subject<number>(22);

	private readonly deathScreenSession = new Subject<IDeathScreenSession | null>(
		{
			sequenceId: 1,
			killerNickname: 'Senades',
			durationMs: 5000,
		},
	);
	private readonly lastEarnedExp = new Subject<number | null>(null);
	private readonly playerTalking = new Subject(false);
	private readonly gender = new Subject<Gender>(Gender.MALE);
	private readonly hudHelpButtonsVisible = new Subject<boolean>(true);
	private readonly techTuningDragHintShowed = new Subject<boolean>(false);
	private readonly playerLevel = new Subject<IPlayerLevel>({
		exp: 150,
		nextLevelExp: 1000,
		level: 15,
		levelProgress: 0.5,
	});

	private getPriceWithDiscount(priceConfig: IPriceConfig): number {
		const now = Date.now();
		if (!priceConfig.discount) {
			return priceConfig.price;
		}

		if (
			priceConfig.discountTimestampStart === null ||
			priceConfig.discountTimestampEnd === null
		) {
			return priceConfig.price;
		}

		if (
			now < priceConfig.discountTimestampStart ||
			now > priceConfig.discountTimestampEnd
		) {
			return priceConfig.price;
		}

		return Math.floor(priceConfig.price * (1 - priceConfig.discount));
	}

	constructor(private readonly modal: IModalService) {
		(global as any).setPlayerLevel = (level: IPlayerLevel) => {
			const oldLevel = this.playerLevel.getValue();
			const newLevel = {
				...oldLevel,
				...level,
			};

			let expDiff = 0;

			if (newLevel.level === oldLevel.level) {
				expDiff = newLevel.exp - oldLevel.exp;
			} else if (newLevel.level > oldLevel.level) {
				expDiff = oldLevel.nextLevelExp - oldLevel.exp + newLevel.exp;
			}

			this.playerLevel.next(newLevel);

			if (expDiff > 0) {
				setTimeout(() => {
					this.lastEarnedExp.next(expDiff);
				}, 50);
			}
		};
	}

	public getAccountId(): IObservable<string | null> {
		return this.accountId;
	}

	public getCharacterId(): IObservable<string | null> {
		return this.characterId;
	}

	public getNetId(): IObservable<number | null> {
		return this.netId;
	}

	public getPremium(): IObservable<boolean> {
		return this.premium;
	}

	public getPremiumDays(): IObservable<number> {
		return this.premiumDays;
	}

	public getNickname(): IObservable<string> {
		return this.nickname;
	}

	public getUsedParkingSlots(): IObservable<number> {
		return this.usedParkingSlots;
	}

	public getTotalParkingSlots(): IObservable<number> {
		return this.totalParkingSlots;
	}

	public async waitForAccountLogIn(): Promise<void> {
		await sleep(0);
	}

	public async waitForCharacterSelect(): Promise<void> {
		await sleep(0);
	}

	@devSimulatePing(1000)
	public async buyParkingSlots(count: number): Promise<void> {
		this.totalParkingSlots.next(this.totalParkingSlots.getValue() + count);
	}

	public showBuyParkingSlotsModal(): void {
		this.modal.showModal(
			{
				type: ModalType.CountAndPrice,
				title: 'Гаражные места',
				text: '',
				acceptButton: { text: 'Оплатить', color: ModalButtonColor.Success },
				cancelButton: { text: 'Отменить', color: ModalButtonColor.Secondary },
				count: 1,
				maxCount: 10,
				minCount: 1,
				pricePerItem: this.getPriceWithDiscount(
					this.parkingSlotPriceConfig.getValue(),
				),
				wallet: WalletType.Donate,
			},
			(result) => {
				if (result.isAccepted) {
					this.buyParkingSlots(1);
				}
			},
		);
	}

	public getDeathScreenSession(): IObservable<IDeathScreenSession | null> {
		return this.deathScreenSession;
	}

	public getPlayerLevel(): IObservable<IPlayerLevel> {
		return this.playerLevel;
	}

	public getLastEarnedExp(): IObservable<number | null> {
		return this.lastEarnedExp;
	}

	public clearLastEarnedExp(): void {
		this.lastEarnedExp.next(null);
	}

	public getPlayerIsTalking(): IObservable<boolean> {
		return this.playerTalking;
	}

	public getPlayerGender(): IObservable<Gender> {
		return this.gender;
	}

	public getPlayerRegistrationDate(): IObservable<number> {
		return this.registrationDate;
	}

	public getParkingSlotsPrice(): IObservable<number> {
		return this.parkingSlotsPrice;
	}

	public getChangeNamePrice(): IObservable<number> {
		return this.changeNamePrice;
	}

	public getParkingSlotPriceConfig(): IObservable<IPriceConfig> {
		return this.parkingSlotPriceConfig;
	}

	public getChangeNamePriceConfig(): IObservable<IPriceConfig> {
		return this.changeNamePriceConfig;
	}

	public getPlayerJailInfo(): IObservable<IPlayerJailInfo | null> {
		return this.playerJailInfo;
	}

	public getHudHelpButtonsVisible(): IObservable<boolean> {
		return this.hudHelpButtonsVisible;
	}

	public async setHudHelpButtonsVisible(value: boolean): Promise<void> {
		this.hudHelpButtonsVisible.next(value);
	}

	public getTechTuningDragHintShowed(): IObservable<boolean> {
		return this.techTuningDragHintShowed;
	}

	public async setTechTuningDragHintShowed(value: boolean): Promise<void> {
		this.techTuningDragHintShowed.next(value);
	}

	public getMaskedEmail(): IObservable<string> {
		return this.maskedEmail;
	}
}
